// Modern Seller Onboarding Component
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { STRIPE_CONFIG } from '../services/stripe';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../constants';

// Modern SVG Icons
const RocketIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22,4 12,14.01 9,11.01"></polyline>
  </svg>
);

const ClockIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const DollarSignIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <polyline points="9,12 12,15 22,6"></polyline>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
    <polyline points="17,6 23,6 23,12"></polyline>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

function SellerOnboarding({ user, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sellerStatus, setSellerStatus] = useState('loading');
  const [accountLink, setAccountLink] = useState(null);
  const [primaryButtonHover, setPrimaryButtonHover] = useState(false);
  const [secondaryButtonHover, setSecondaryButtonHover] = useState(false);
  const [hasListings, setHasListings] = useState(false);

  const checkSellerStatus = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      // Check if user has any listings
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const listingsQuery = query(
        collection(db, 'listings'),
        where('sellerId', '==', user.uid)
      );
      const listingsSnap = await getDocs(listingsQuery);
      setHasListings(listingsSnap.size > 0);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const localStatus = userData.sellerStatus || 'not_started';
        setSellerStatus(localStatus);
        
        // If they have a Stripe account, get live status
        if (userData.stripeConnectAccountId) {
          if (process.env.REACT_APP_USE_EMULATORS === "true") {
            // In development, just use the cached status from Firestore
          } else {
            try {
              const { httpsCallable } = await import('firebase/functions');
              const { functions } = await import('../services/firebase');
              
              const getSellerStatus = httpsCallable(functions, 'getSellerStatus');
              const result = await getSellerStatus();
              
              if (result.success && result.data) {
                setSellerStatus(result.data.status);
              }
            } catch (statusError) {
              }
          }
        }
      } else {
        setSellerStatus('not_started');
      }
    } catch (err) {
      setError('Failed to check seller status');
      setSellerStatus('not_started');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkSellerStatus();
  }, [checkSellerStatus]);

  const startSellerOnboarding = async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      
      // Development mode: Mock Stripe Connect account creation
      if (process.env.REACT_APP_USE_EMULATORS === "true") {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock successful account creation
        result = {
          data: {
            accountId: `acct_dev_${user.uid.slice(0, 8)}`,
            onboardingUrl: `${window.location.origin}/seller-success?mock=true&account=${user.uid.slice(0, 8)}`,
            success: true
          }
        };
        
        } else {
        // Production mode: Call actual Firebase Function
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../services/firebase');
        
        const createSellerAccount = httpsCallable(functions, 'createSellerAccount');
        
        result = await createSellerAccount({
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
        });
      }
      
      // Store the account link for user to complete onboarding
      setAccountLink(result.data.onboardingUrl);
      
      // Update user status in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        stripeConnectAccountId: result.data.accountId,
        sellerStatus: 'pending',
        sellerOnboardingStarted: new Date(),
      });

      setSellerStatus('pending');
      
    } catch (err) {
      setError(err.message || 'Failed to start seller setup');
    } finally {
      setLoading(false);
    }
  };

  const continueOnboarding = async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      
      // Development mode: Mock refresh onboarding link
      if (process.env.REACT_APP_USE_EMULATORS === "true") {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Mock fresh onboarding link
        result = {
          data: {
            onboardingUrl: `${window.location.origin}/seller-success?mock=true&refresh=${Date.now()}&account=${user.uid.slice(0, 8)}`,
            success: true
          }
        };
        
        } else {
        // Production mode: Call actual Firebase Function
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../services/firebase');
        
        const createSellerAccount = httpsCallable(functions, 'createSellerAccount');
        
        result = await createSellerAccount({
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
        });
      }

      setAccountLink(result.data.onboardingUrl);
      
    } catch (err) {
      setError(err.message || 'Failed to refresh onboarding');
    } finally {
      setLoading(false);
    }
  };

  const renderLoadingState = () => (
    <div style={modernStyles.container}>
      <div style={modernStyles.heroSection}>
        <div style={modernStyles.iconContainer}>
          <div style={modernStyles.spinner}></div>
        </div>
        <h1 style={modernStyles.heroTitle}>Loading Your Account...</h1>
        <p style={modernStyles.heroSubtitle}>
          We're checking your seller account status. This will just take a moment.
        </p>
      </div>
    </div>
  );

  const renderStatus = () => {
    // Show loading state while checking status
    if (loading || sellerStatus === 'loading') {
      return renderLoadingState();
    }

    switch (sellerStatus) {
      case 'not_started':
        return (
          <div style={modernStyles.container}>
            {/* Hero Section */}
            <div style={modernStyles.heroSection}>
              <div style={modernStyles.iconContainer}>
                <RocketIcon />
              </div>
              <h1 style={modernStyles.heroTitle}>Start Your Selling Journey</h1>
              <p style={modernStyles.heroSubtitle}>
                Join the ISU student marketplace community. Set up your seller account 
                in just 5 minutes and start earning today!
              </p>
            </div>

            {/* Benefits Grid */}
            <div style={modernStyles.benefitsSection}>
              <h2 style={modernStyles.sectionTitle}>Why Sell on Redbird Markets?</h2>
              <div style={modernStyles.benefitsGrid}>
                <div style={modernStyles.benefitCard}>
                  <div style={modernStyles.benefitIcon}>
                    <DollarSignIcon />
                  </div>
                  <h3 style={modernStyles.benefitTitle}>Direct Payments</h3>
                  <p style={modernStyles.benefitDescription}>
                    Get paid directly to your bank account with next-business-day transfers
                  </p>
                </div>

                <div style={modernStyles.benefitCard}>
                  <div style={modernStyles.benefitIcon}>
                    <ShieldIcon />
                  </div>
                  <h3 style={modernStyles.benefitTitle}>Secure & Protected</h3>
                  <p style={modernStyles.benefitDescription}>
                    Industry-leading security with buyer and seller protection included
                  </p>
                </div>

                <div style={modernStyles.benefitCard}>
                  <div style={modernStyles.benefitIcon}>
                    <TrendingUpIcon />
                  </div>
                  <h3 style={modernStyles.benefitTitle}>Track Performance</h3>
                  <p style={modernStyles.benefitDescription}>
                    Monitor your sales, earnings, and analytics in your personal dashboard
                  </p>
                </div>

                <div style={modernStyles.benefitCard}>
                  <div style={modernStyles.benefitIcon}>
                    <UsersIcon />
                  </div>
                  <h3 style={modernStyles.benefitTitle}>ISU Community</h3>
                  <p style={modernStyles.benefitDescription}>
                    Connect with verified ISU students in a trusted campus marketplace
                  </p>
                </div>
              </div>

              {/* Platform Fee Info */}
              <div style={modernStyles.feeCard}>
                <div style={modernStyles.feeContent}>
                  <h3 style={modernStyles.feeTitle}>Seller Fees</h3>
                  <div style={modernStyles.feeAmount}>
                    {(STRIPE_CONFIG.platformFeePercentage * 100)}% + Stripe
                  </div>
                  <p style={modernStyles.feeDescription}>
                    {(STRIPE_CONFIG.platformFeePercentage * 100)}% platform fee + Stripe processing (2.9% + $0.30). No monthly charges.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div style={modernStyles.ctaSection}>
              <button 
                onClick={startSellerOnboarding}
                disabled={loading}
                onMouseEnter={() => setPrimaryButtonHover(true)}
                onMouseLeave={() => setPrimaryButtonHover(false)}
                style={{
                  ...modernStyles.primaryButton,
                  ...(primaryButtonHover && !loading ? modernStyles.primaryButtonHover : {}),
                  ...(loading ? modernStyles.buttonDisabled : {})
                }}
              >
                {loading ? (
                  <>
                    <div style={modernStyles.spinner}></div>
                    Setting up account...
                  </>
                ) : (
                  <>
                    <RocketIcon />
                    <span>Become a Seller</span>
                  </>
                )}
              </button>
              <p style={modernStyles.ctaNote}>
                Setup takes less than 5 minutes • Powered by Stripe
              </p>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div style={modernStyles.container}>
            <div style={modernStyles.heroSection}>
              <div style={modernStyles.iconContainer}>
                <ClockIcon />
              </div>
              <h1 style={modernStyles.heroTitle}>Complete Your Setup</h1>
              <p style={modernStyles.heroSubtitle}>
                You've started the seller registration process. Complete your setup with Stripe 
                to start receiving payments from fellow Redbirds.
              </p>
            </div>

            {/* Progress Steps */}
            <div style={modernStyles.stepsSection}>
              <div style={modernStyles.stepItem}>
                <div style={modernStyles.stepIcon}>✓</div>
                <div style={modernStyles.stepContent}>
                  <h3 style={modernStyles.stepTitle}>Account Created</h3>
                  <p style={modernStyles.stepDescription}>Your seller account has been initialized</p>
                </div>
              </div>
              <div style={modernStyles.stepItem}>
                <div style={modernStyles.stepIconPending}>2</div>
                <div style={modernStyles.stepContent}>
                  <h3 style={modernStyles.stepTitle}>Complete Stripe Setup</h3>
                  <p style={modernStyles.stepDescription}>Verify your identity and bank details</p>
                </div>
              </div>
              <div style={modernStyles.stepItem}>
                <div style={modernStyles.stepIconDisabled}>3</div>
                <div style={modernStyles.stepContent}>
                  <h3 style={modernStyles.stepTitleDisabled}>Start Selling</h3>
                  <p style={modernStyles.stepDescription}>List your first item and earn money</p>
                </div>
              </div>
            </div>

            <div style={modernStyles.ctaSection}>
              {accountLink ? (
                <a 
                  href={accountLink} 
                  target={process.env.REACT_APP_USE_EMULATORS === "true" ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  style={modernStyles.primaryButton}
                >
                  <span>Continue Setup with Stripe</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15,3 21,3 21,9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              ) : (
                <button 
                  onClick={continueOnboarding}
                  disabled={loading}
                  onMouseEnter={() => setSecondaryButtonHover(true)}
                  onMouseLeave={() => setSecondaryButtonHover(false)}
                  style={{
                    ...modernStyles.secondaryButton,
                    ...(secondaryButtonHover && !loading ? modernStyles.secondaryButtonHover : {}),
                    ...(loading ? modernStyles.buttonDisabled : {})
                  }}
                >
                  {loading ? (
                    <>
                      <div style={modernStyles.spinner}></div>
                      Getting link...
                    </>
                  ) : (
                    <>
                      <span>Get Setup Link</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23,4 23,10 17,10"></polyline>
                        <polyline points="1,20 1,14 7,14"></polyline>
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64l-.85.85"></path>
                        <path d="M3.51 15a9 9 0 0 0 14.85 3.36l.85-.85"></path>
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );

      case 'active':
        return (
          <div style={modernStyles.container}>
            <div style={modernStyles.heroSection}>
              <div style={modernStyles.iconContainer}>
                <CheckCircleIcon />
              </div>
              <h1 style={modernStyles.heroTitle}>You're All Set!</h1>
              <p style={modernStyles.heroSubtitle}>
                Congratulations! Your seller account is active and ready to start earning money 
                on Redbird Markets.
              </p>
            </div>

            {/* Success Stats */}
            <div style={modernStyles.successStats}>
              <div style={modernStyles.statCard}>
                <div style={modernStyles.statIcon}>
                  <DollarSignIcon />
                </div>
                <div style={modernStyles.statContent}>
                  <span style={modernStyles.statLabel}>Fees Per Sale</span>
                  <span style={modernStyles.statValue}>{(STRIPE_CONFIG.platformFeePercentage * 100)}% + Stripe</span>
                  <span style={{fontSize: '0.75rem', color: COLORS.text.muted, marginTop: '2px'}}>
                    Plus Stripe's 2.9% + $0.30
                  </span>
                </div>
              </div>
              <div style={modernStyles.statCard}>
                <div style={modernStyles.statIcon}>
                  <TrendingUpIcon />
                </div>
                <div style={modernStyles.statContent}>
                  <span style={modernStyles.statLabel}>Payout Schedule</span>
                  <span style={modernStyles.statValue}>Next business day</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div style={modernStyles.nextStepsSection}>
              <h2 style={modernStyles.sectionTitle}>What's Next?</h2>
              <div style={modernStyles.nextStepsList}>
                <div style={modernStyles.nextStepItem}>
                  <span style={modernStyles.nextStepNumber}>1</span>
                  <span>{hasListings ? 'Manage your active listings' : 'Create your first listing with photos and description'}</span>
                </div>
                <div style={modernStyles.nextStepItem}>
                  <span style={modernStyles.nextStepNumber}>2</span>
                  <span>Set competitive prices for fellow ISU students</span>
                </div>
                <div style={modernStyles.nextStepItem}>
                  <span style={modernStyles.nextStepNumber}>3</span>
                  <span>{hasListings ? 'Keep selling and earning money' : 'Start earning money from your unused items'}</span>
                </div>
              </div>
            </div>

            <div style={modernStyles.ctaSection}>
              <button 
                onClick={onComplete}
                style={modernStyles.primaryButton}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
                <span>{hasListings ? 'Create Another Listing' : 'Create Your First Listing'}</span>
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="onboarding-error">
            <span className="status-icon">❓</span>
            <h2>Unknown Status</h2>
            <p>Please try refreshing the page or contact support.</p>
          </div>
        );
    }
  };

  return (
    <div style={modernStyles.wrapper}>
      {renderStatus()}
      
      {error && (
        <div style={modernStyles.errorCard}>
          <div style={modernStyles.errorIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <div style={modernStyles.errorContent}>
            <h3 style={modernStyles.errorTitle}>Setup Error</h3>
            <p style={modernStyles.errorMessage}>{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

  // Modern Styles
const modernStyles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${LAYOUT.spacing.xl} ${LAYOUT.spacing.lg}`,
  },
  
  container: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.xxl}`,
    boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 32px -8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${COLORS.border}`,
    textAlign: 'center',
  },
  
  // Hero Section
  heroSection: {
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '120px',
    height: '120px',
    backgroundColor: `${COLORS.primary}15`,
    borderRadius: '50%',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
    color: COLORS.primary,
  },
  
  heroTitle: {
    ...TYPOGRAPHY.heading1,
    fontSize: '3rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
    lineHeight: '1.1',
    '@media (max-width: 768px)': {
      fontSize: '2.5rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '2rem',
    },
  },
  
  heroSubtitle: {
    fontSize: '1.2rem',
    color: COLORS.text.secondary,
    lineHeight: '1.65',
    maxWidth: '580px',
    margin: '0 auto',
    fontWeight: '400',
  },
  
  // Benefits Section
  benefitsSection: {
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  sectionTitle: {
    ...TYPOGRAPHY.heading2,
    fontSize: '1.8rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xl,
    letterSpacing: '-0.01em',
  },
  
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.xl,
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: LAYOUT.spacing.md,
    },
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.sm,
    },
  },
  
  benefitCard: {
    textAlign: 'center',
    padding: LAYOUT.spacing.xl,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
    transition: 'all 0.3s ease',
    cursor: 'default',
  },
  
  benefitIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 auto ${LAYOUT.spacing.md}`,
    color: COLORS.white,
  },
  
  benefitTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.sm,
  },
  
  benefitDescription: {
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
    lineHeight: '1.5',
  },
  
  // Fee Card
  feeCard: {
    background: `linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.primary}08)`,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: `${LAYOUT.spacing.xl} ${LAYOUT.spacing.xxl}`,
    border: `1px solid ${COLORS.primary}25`,
    textAlign: 'center',
    boxShadow: `0 4px 16px rgba(206, 17, 38, 0.08)`,
  },
  
  feeContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
  },
  
  feeTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: COLORS.text.primary,
  },
  
  feeAmount: {
    fontSize: '2.5rem',
    fontWeight: '800',
    color: COLORS.primary,
  },
  
  feeDescription: {
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
    margin: 0,
  },
  
  // Steps Section (for pending state)
  stepsSection: {
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.lg,
    padding: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.md,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
  },
  
  stepIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.success,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    fontWeight: '700',
    fontSize: '1.2rem',
  },
  
  stepIconPending: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    fontWeight: '700',
    fontSize: '1.2rem',
  },
  
  stepIconDisabled: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.text.muted,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    fontWeight: '700',
    fontSize: '1.2rem',
  },
  
  stepContent: {
    flex: 1,
  },
  
  stepTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.xs,
  },
  
  stepTitleDisabled: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: COLORS.text.muted,
    marginBottom: LAYOUT.spacing.xs,
  },
  
  stepDescription: {
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
    margin: 0,
  },
  
  // Success Stats
  successStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    padding: LAYOUT.spacing.xl,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
  },
  
  statIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
  },
  
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xs,
  },
  
  statLabel: {
    fontSize: '0.85rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },
  
  statValue: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: COLORS.text.primary,
  },
  
  // Next Steps
  nextStepsSection: {
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  nextStepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
  },
  
  nextStepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    padding: LAYOUT.spacing.md,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
  },
  
  nextStepNumber: {
    width: '32px',
    height: '32px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    fontWeight: '700',
    fontSize: '0.9rem',
  },
  
  // CTA Section
  ctaSection: {
    textAlign: 'center',
  },
  
  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    minWidth: '280px',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxxl}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: '50px', // More modern pill shape
    fontSize: '1.1rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    textDecoration: 'none',
    boxShadow: `0 4px 24px rgba(206, 17, 38, 0.3), 0 2px 8px rgba(206, 17, 38, 0.2)`,
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    transform: 'translateY(0)',
  },
  
  primaryButtonHover: {
    backgroundColor: COLORS.primaryDark,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px rgba(206, 17, 38, 0.4), 0 4px 16px rgba(206, 17, 38, 0.3)`,
  },
  
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    minWidth: '280px',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxxl}`,
    backgroundColor: 'transparent',
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '50px', // Match primary button
    fontSize: '1.1rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    textDecoration: 'none',
    textTransform: 'uppercase',
    transform: 'translateY(0)',
  },
  
  secondaryButtonHover: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 16px rgba(206, 17, 38, 0.2)`,
  },
  
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
  },
  
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  
  ctaNote: {
    marginTop: LAYOUT.spacing.lg,
    fontSize: '0.9rem',
    color: COLORS.text.muted,
  },
  
  // Error Card
  errorCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.md,
    marginTop: LAYOUT.spacing.xl,
    padding: LAYOUT.spacing.xl,
    backgroundColor: '#fef2f2',
    borderRadius: LAYOUT.borderRadius.lg,
    border: '1px solid #fecaca',
  },
  
  errorIcon: {
    width: '40px',
    height: '40px',
    backgroundColor: '#dc2626',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    flexShrink: 0,
  },
  
  errorContent: {
    flex: 1,
  },
  
  errorTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: LAYOUT.spacing.xs,
  },
  
  errorMessage: {
    fontSize: '0.95rem',
    color: '#7f1d1d',
    margin: 0,
    lineHeight: '1.5',
  },
};

export default SellerOnboarding;